/**
 * Dynamic LCR (Layer Coefficient Ratio) Calculation Service
 * Based on exact Excel polynomial equations from DATABASE.txt
 *
 * This service replicates Excel's dynamic LCR calculation where LCR values
 * are computed based on CBR values using polynomial equations rather than
 * using hardcoded values.
 *
 * SINGLE SOURCE OF TRUTH: LCR equations are loaded via GeogridLcrBridge,
 * which reads from ProductDataService (API-backed, admin-editable unified JSON)
 * with pavement-database.json as fallback.
 */

import { GeogridLcrBridge, LcrEquation, GeogridLcrEntry } from './GeogridLcrBridge';

// Re-export for backward compatibility
export type { LcrEquation };

export interface GeogridLcrData {
    id: string;
    name: string;
    equation: LcrEquation;
    databaseReference?: string;
}

/**
 * LCR Calculation Service
 * Implements Excel's exact logic for dynamic LCR calculation
 */
export class LcrCalculationService {
    /**
     * Calculate dynamic LCR based on CBR using Excel polynomial equations
     * Replicates Excel formula: =IF(CBR<threshold, m2*CBR^2+m1*CBR+c, m2*threshold^2+m1*threshold+c)
     *
     * @param geogridId Geogrid identifier
     * @param cbr CBR value from material properties
     * @returns Calculated LCR value
     */
    calculateDynamicLCR(geogridId: string, cbr: number): number {
        const equation = GeogridLcrBridge.getEquation(geogridId);

        if (!equation) {
            return 1.0;
        }

        // Excel logic: if CBR < threshold, use CBR; otherwise use threshold
        const effectiveCBR = Math.min(cbr, equation.threshold);

        // Calculate LCR using polynomial equation: m2*CBR² + m1*CBR + c
        const lcr = equation.m2 * Math.pow(effectiveCBR, 2) + equation.m1 * effectiveCBR + equation.c;

        return lcr;
    }

    /**
     * Calculate LCR for base layer geogrid
     * IMPORTANT: Excel uses subbase CBR value for base geogrid calculations (DATABASE.txt E30)
     * This matches Excel's logic: =IF(Design_LCR!G27=0,Design_LCR!G41,Design_LCR!N34)
     *
     * @param baseGeogridId Base geogrid identifier
     * @param subbaseCbr CBR value of subbase material (Excel uses this for base geogrid)
     * @returns Base LCR value
     */
    calculateBaseLCR(baseGeogridId: string | null, subbaseCbr: number): number {
        if (!baseGeogridId || baseGeogridId === 'None' || baseGeogridId === 'none' || baseGeogridId === 'none_selected') {
            return 1.0;
        }

        // Excel logic: Use subbase CBR for base geogrid LCR calculation
        return this.calculateDynamicLCR(baseGeogridId, subbaseCbr);
    }

    /**
     * Calculate LCR for subbase layer geogrid
     * Uses subbase material CBR value, but if not available, uses subgrade CBR
     *
     * @param subbaseGeogridId Subbase geogrid identifier
     * @param subbaseCbr CBR value of subbase material
     * @param subgradeCbr CBR value of subgrade (fallback)
     * @returns Subbase LCR value
     */
    calculateSubbaseLCR(subbaseGeogridId: string | null, subbaseCbr: number, subgradeCbr?: number): number {
        if (!subbaseGeogridId || subbaseGeogridId === 'None' || subbaseGeogridId === 'none' || subbaseGeogridId === 'none_selected') {
            return 1.0;
        }

        // Excel logic: use subbase CBR if available, otherwise use subgrade CBR
        const effectiveCbr = subbaseCbr > 0 ? subbaseCbr : subgradeCbr || 2;

        return this.calculateDynamicLCR(subbaseGeogridId, effectiveCbr);
    }

    /**
     * Get all available geogrid equations
     * @returns Array of geogrid LCR data with database references
     */
    getAvailableGeogrids(): GeogridLcrData[] {
        return GeogridLcrBridge.getEntries().map((entry: GeogridLcrEntry) => ({
            id: entry.id,
            name: entry.name,
            equation: entry.equation,
            databaseReference: entry.databaseReference,
        }));
    }

    /**
     * Validate CBR value
     * @param cbr CBR value to validate
     * @returns Validated CBR value (minimum 0.1)
     */
    validateCBR(cbr: number): number {
        if (cbr <= 0) {
            return 0.1;
        }
        return cbr;
    }
}

/**
 * Singleton instance of LCR calculation service
 */
export const lcrCalculationService = new LcrCalculationService();
